import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { useSchoolBrand } from '../hooks/useSchoolBrand';
import TermMatrixTables from './TermMatrixTables';

// Shared Term-Based Grade Report for Student + Parent portals.
// Props: endpoint (e.g. '/student/grade-report'; falsy = nothing to load), title, resultsPath, accent.
// Renders the backend termMatrix (one table per term) via the shared TermMatrixTables component.

const css = `
  .tgr * { box-sizing:border-box; }
  .tgr { font-family:'Sora',sans-serif; color:#1a1d2e; }
  .tgr-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.2rem; flex-wrap:wrap; gap:1rem; }
  .tgr-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tgr-sub { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .tgr-btn { display:inline-flex; align-items:center; gap:7px; padding:6px 11px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; background:#f0f2f7; color:#6b7194; }
  .tgr-btn:hover { background:#e4e7f0; }
  .tgr-btn svg { width:15px; height:15px; stroke-width:2; }
  .tgr-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; color:#1a1d2e; outline:none; width:130px; }
  .tgr-loading { display:flex; align-items:center; justify-content:center; height:180px; gap:10px; color:#9094a8; }
  .tgr-spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:var(--tgr-accent,#3b6ef8); border-radius:50%; animation:tgrSpin .7s linear infinite; }
  @keyframes tgrSpin { to{transform:rotate(360deg);} }
  .tgr-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; background:white; border-radius:14px; border:1px solid #e8ebf4; }
  .tgr .report-card { background:white; border:2px solid #1a1d2e; border-radius:3px; max-width:860px; margin:0 auto; font-family:'Times New Roman',serif; font-size:13px; color:#111; }
  .rc-header { text-align:center; padding:16px 20px 10px; border-bottom:2px solid #1a1d2e; }
  .rc-logo   { max-height:60px; max-width:180px; margin:0 auto 6px; display:block; }
  .rc-school-name { font-size:1.25rem; font-weight:700; text-transform:uppercase; letter-spacing:.08em; }
  .rc-year        { font-size:.88rem; font-weight:700; margin-top:5px; }
  .rc-student-info { display:grid; grid-template-columns:1fr 1fr; border-bottom:1px solid #1a1d2e; }
  .rc-info-cell   { padding:7px 14px; font-size:.82rem; border-right:1px solid #1a1d2e; }
  .rc-info-cell:nth-child(even) { border-right:none; }
  .rc-info-label  { font-weight:700; margin-right:6px; }
  .rc-subject-section { border-bottom:1px solid #1a1d2e; }
  .rc-subject-name { background:#f0f0f0; padding:6px 14px; font-size:.8rem; font-weight:700; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #1a1d2e; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:4px; }
  .rc-pct-badges { display:flex; gap:6px; font-size:.72rem; font-weight:400; flex-wrap:wrap; }
  .rc-pct-badge  { background:white; border:1px solid #ccc; padding:2px 7px; border-radius:3px; white-space:nowrap; }
  .rc-marks-wrap  { overflow-x:auto; }
  .rc-marks-table { width:100%; border-collapse:collapse; font-size:.76rem; }
  .rc-marks-table th { background:#e8e8e8; padding:5px 10px; text-align:center !important; border:1px solid #ccc; font-weight:700; font-size:.7rem; white-space:nowrap; }
  .rc-marks-table td { padding:5px 10px; text-align:center !important; border:1px solid #ddd; vertical-align:middle; }
  .rc-marks-table td.lbl { text-align:left !important; font-weight:600; }
  .rc-marks-table th.l { text-align:left !important; }
  .tgr-chips { display:flex; flex-wrap:wrap; gap:4px 6px; }
  .tgr-chip { background:#f6f7fb; border:1px solid #dfe2ee; border-radius:4px; padding:1px 7px; font-size:.72rem; font-weight:600; white-space:nowrap; }
  .rc-overall { display:flex; align-items:center; justify-content:space-between; padding:9px 14px; border-bottom:1px solid #1a1d2e; font-size:.86rem; flex-wrap:wrap; gap:6px; }
  .rc-overall-label { font-weight:700; }
  .rc-overall-pct   { font-size:1.15rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
  .rc-attendance { border-bottom:1px solid #1a1d2e; }
  .rc-att-title  { background:#f0f0f0; padding:6px 14px; font-size:.8rem; font-weight:700; border-bottom:1px solid #1a1d2e; }
  .rc-att-table  { width:100%; border-collapse:collapse; font-size:.78rem; }
  .rc-att-table th { background:#e8e8e8; padding:5px 12px; text-align:center !important; border:1px solid #ccc; font-weight:700; }
  .rc-att-table td { padding:5px 12px; text-align:center !important; border:1px solid #ddd; }
  .rc-footer { display:grid; grid-template-columns:1fr 1fr; padding:16px 20px 14px; gap:20px; }
  .rc-sig-block { text-align:center; }
  .rc-sig-line  { border-top:1px solid #1a1d2e; padding-top:5px; font-size:.78rem; margin-top:30px; }
  .rc-marks-cards { display:none; }
  .rc-mc-row { display:flex; justify-content:space-between; align-items:center; padding:6px 14px; font-size:.78rem; border-bottom:1px solid #eee; gap:8px; }
  .rc-mc-row:last-child { border-bottom:none; background:#f8f8f8; font-weight:700; }
  .rc-mc-label { font-weight:600; }
  .rc-att-cards { display:none; padding:4px 14px 8px; }
  .rc-att-mrow { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #eee; font-size:.78rem; }
  .rc-att-mrow:last-child { border-bottom:none; }
  .rc-att-mlabel { font-weight:600; }
  @media print {
    body * { visibility:hidden !important; }
    .tgr-print-area, .tgr-print-area * { visibility:visible !important; }
    .tgr-print-area { position:fixed; left:0; top:0; width:100%; }
  }
  @media (max-width:768px) {
    .tgr-title { font-size:1.1rem; }
    .rc-student-info { grid-template-columns:1fr; }
    .rc-info-cell { border-right:none; border-bottom:1px solid #ddd; }
    .rc-footer { grid-template-columns:1fr; }
    .rc-marks-wrap { display:none !important; }
    .rc-marks-cards { display:block; }
    .rc-att-table-wrap { display:none !important; }
    .rc-att-cards { display:block; }
  }
`;

const curYear = () => { const y = new Date().getFullYear(); return `${y}-${y + 1}`; };
const pctColor = p => p >= 80 ? '#10c98e' : p >= 60 ? '#f59e0b' : '#ef4444';
const pctText = p => (p != null ? p + '%' : '—');
const dash = v => v || '—';

export default function TermGradeReportCard({ endpoint, title, resultsPath, accent = '#3b6ef8' }) {
  const { brand } = useSchoolBrand();
  const [year, setYear] = useState(curYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!endpoint) return undefined;
    const ctrl = new AbortController();
    setLoading(true);
    api.get(`${endpoint}?academicYear=${encodeURIComponent(year)}`, { signal: ctrl.signal })
      .then(({ data: r }) => setData(r))
      .catch(e => {
        if (e.code === 'ERR_CANCELED') return;
        setData(null);
        toast.error('Failed to load grade report');
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [endpoint, year]);

  const att = (data && data.attendance) || {};
  const st = (data && data.student) || {};
  const notTerm = data && data.mode && data.mode !== 'term';
  const hasSubjects = data && ((data.termMatrix && (data.termMatrix.terms || []).length > 0) || (data.subjectResults || []).length > 0);

  return (
    <div className="tgr" style={{ '--tgr-accent': accent }}>
      <style>{css}</style>
      <div className="tgr-hdr">
        <div>
          <div className="tgr-title">{title || 'Grade Report'}</div>
          <div className="tgr-sub">Term-wise progress report</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="text" className="tgr-input" value={year} onChange={e => setYear(e.target.value)} placeholder="2025-2026" />
          {data && !notTerm && hasSubjects && <button className="tgr-btn" onClick={() => window.print()}><PrinterIcon />Print</button>}
        </div>
      </div>

      {loading ? (
        <div className="tgr-loading"><div className="tgr-spinner" />Loading…</div>
      ) : !data ? (
        <div className="tgr-empty">Could not load the grade report.</div>
      ) : notTerm ? (
        <div className="tgr-empty">
          The grade report is available for schools using Term-Based grading — see{' '}
          {resultsPath ? <Link to={resultsPath} style={{ color: accent, fontWeight: 600 }}>Results</Link> : 'Results'}.
        </div>
      ) : !hasSubjects ? (
        <div className="tgr-empty">No grade report available for {year}.</div>
      ) : (
        <div className="tgr-print-area">
          <div className="report-card">
            <div className="rc-header">
              {brand.logo && <img src={brand.logo} className="rc-logo" alt="logo" />}
              <div className="rc-school-name">{brand.name || 'School Name'}</div>
              <div className="rc-year">Academic Year: {data.academicYear || year} &nbsp;|&nbsp; Progress Report</div>
            </div>

            <div className="rc-student-info">
              <div className="rc-info-cell"><span className="rc-info-label">Name:</span>{st.name}</div>
              <div className="rc-info-cell"><span className="rc-info-label">Reg No:</span>{st.admissionNo}</div>
              <div className="rc-info-cell"><span className="rc-info-label">Grade:</span>{st.class} – {st.section}</div>
              <div className="rc-info-cell"><span className="rc-info-label">Roll No:</span>{st.rollNo}</div>
            </div>

            <TermMatrixTables matrix={data.termMatrix} />

            <div className="rc-overall">
              <span className="rc-overall-label">Overall Percentage:</span>
              <span className="rc-overall-pct" style={{ color: data.overallPercentage != null ? pctColor(data.overallPercentage) : '#1a1d2e' }}>
                {pctText(data.overallPercentage)}
              </span>
            </div>

            <div className="rc-attendance">
              <div className="rc-att-title">Attendance Summary</div>
              <div className="rc-att-table-wrap" style={{ overflowX: 'auto' }}>
                <table className="rc-att-table">
                  <thead>
                    <tr><th>Total Days</th><th>Present</th><th>Absent</th><th>Late</th><th>On Leave</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{dash(att.totalDays)}</td><td>{dash(att.present)}</td><td>{dash(att.absent)}</td>
                      <td>{dash(att.late)}</td><td>{dash(att.onLeave)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="rc-att-cards">
                <div className="rc-att-mrow"><span className="rc-att-mlabel">Total Days</span><span>{dash(att.totalDays)}</span></div>
                <div className="rc-att-mrow"><span className="rc-att-mlabel">Present</span><span>{dash(att.present)}</span></div>
                <div className="rc-att-mrow"><span className="rc-att-mlabel">Absent</span><span>{dash(att.absent)}</span></div>
                <div className="rc-att-mrow"><span className="rc-att-mlabel">Late</span><span>{dash(att.late)}</span></div>
                <div className="rc-att-mrow"><span className="rc-att-mlabel">On Leave</span><span>{dash(att.onLeave)}</span></div>
              </div>
            </div>

            <div className="rc-footer">
              <div className="rc-sig-block"><div className="rc-sig-line">Class Teacher's Signature</div></div>
              <div className="rc-sig-block"><div className="rc-sig-line">Principal's Signature &amp; School Stamp</div></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
